import type { PortalBranding } from './types';

export const DEFAULT_LOGO_LIGHT = '/branding/logo-light.png';
export const DEFAULT_LOGO_DARK = '/branding/logo-dark.png';

/** Preset accents derived from official light + dark logo artwork */
export const LOGO_BRAND_ACCENT_LIGHT = '#9394ff';
export const LOGO_BRAND_ACCENT_DARK = '#a78bfa';

const FALLBACK_LIGHT = '#6366f1';
const FALLBACK_DARK = '#818cf8';

export function resolveMediaUrl(stored: string | null | undefined, fallbackPath: string): string {
  if (!stored || !String(stored).trim()) return fallbackPath;
  const path = String(stored).trim();
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/api/')) {
    const base = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/$/, '');
    const origin = base.replace(/\/api\/?$/i, '');
    return origin + path;
  }
  return path;
}

function parseHex(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function toHex(r: number, g: number, b: number): string {
  const c = (x: number) =>
    Math.max(0, Math.min(255, Math.round(x)))
      .toString(16)
      .padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

function mixRgb(
  a: { r: number; g: number; b: number },
  b: { r: number; g: number; b: number },
  t: number
) {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  };
}

function shade(hex: string, pct: number): string {
  const p = parseHex(hex);
  if (!p) return hex;
  const f = 1 + pct / 100;
  return toHex(p.r * f, p.g * f, p.b * f);
}

function rgba(hex: string, alpha: number): string {
  const p = parseHex(hex);
  if (!p) return `rgba(99, 102, 241, ${alpha})`;
  return `rgba(${p.r},${p.g},${p.b},${alpha})`;
}

export function accentForMode(branding: PortalBranding, isDark: boolean): string {
  if (branding.color_mode === 'custom') {
    const raw = isDark ? branding.custom_accent_dark : branding.custom_accent_light;
    return parseHex(raw || '') ? raw : isDark ? FALLBACK_DARK : FALLBACK_LIGHT;
  }
  return isDark ? LOGO_BRAND_ACCENT_DARK : LOGO_BRAND_ACCENT_LIGHT;
}

export function applyBrandingCssVars(branding: PortalBranding, isDark: boolean): void {
  const accent = accentForMode(branding, isDark);
  const hover = isDark ? shade(accent, 8) : shade(accent, -12);
  const accentRgb = parseHex(accent);
  const white = { r: 255, g: 255, b: 255 };
  const darkBg = { r: 17, g: 24, b: 39 };

  let mutedBg: string;
  let mutedText: string;
  if (accentRgb) {
    if (isDark) {
      const m = mixRgb(darkBg, accentRgb, 0.2);
      mutedBg = toHex(m.r, m.g, m.b);
      mutedText = shade(accent, 18);
    } else {
      const m = mixRgb(white, accentRgb, 0.1);
      mutedBg = toHex(m.r, m.g, m.b);
      mutedText = shade(accent, -28);
    }
  } else {
    mutedBg = isDark ? '#111827' : '#f5f3ff';
    mutedText = accent;
  }

  const root = document.documentElement;
  root.style.setProperty('--color-brand', accent);
  root.style.setProperty('--color-brand-hover', hover);
  root.style.setProperty('--color-brand-muted', mutedBg);
  root.style.setProperty('--color-brand-muted-text', mutedText);
  root.style.setProperty('--color-brand-border', rgba(accent, isDark ? 0.45 : 0.28));
}

export function defaultBranding(): PortalBranding {
  return {
    logo_light_url: null,
    logo_dark_url: null,
    color_mode: 'brand',
    custom_accent_light: FALLBACK_LIGHT,
    custom_accent_dark: FALLBACK_DARK,
  };
}
